import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, XCircle, TrendingUp, BatteryWarning, Brain } from 'lucide-react';
import { SectionReveal } from '../ui/SectionReveal';
const failedAttempts = [{
  icon: TrendingUp,
  text: 'Las dietas extremas elevan el cortisol, lo que irónicamente puede subir tu glucosa por estrés.'
}, {
  icon: XCircle,
  text: 'Contar calorías no sirve si no corriges primero la resistencia a la insulina desde la raíz.'
}, {
  icon: BatteryWarning,
  text: 'Confiar solo en el ejercicio toma meses para mostrar resultados, mientras tus órganos sufren hoy.'
}, {
  icon: AlertTriangle,
  text: "Falta una red de seguridad: Suplementos específicos que actúan como 'escudos' cuando comes carbohidratos."
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    x: -20
  },
  show: {
    opacity: 1,
    x: 0,
    transition: {
      type: 'spring',
      stiffness: 100
    }
  }
};
export function ProblemSection() {
  return <SectionReveal bgColor="mint" data-id="element-153">
      <div className="max-w-4xl mx-auto" data-id="element-154">
        {/* Hook */}
        <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg shadow-[#0D6E6E]/5 border border-[#0D6E6E]/10 mb-16 relative" data-id="element-155">
          <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-[#D4A017] text-white w-12 h-12 rounded-full flex items-center justify-center shadow-lg" data-id="element-156">
            <Brain className="w-6 h-6" data-id="element-157" />
          </div>
          <p className="text-lg md:text-xl text-gray-700 leading-relaxed text-center font-medium mt-4" data-id="element-158">
            "¿Te ha pasado que, a pesar de cuidarte, tu glucosa se dispara sin
            aviso después de una comida normal? Esa sensación de incertidumbre y
            el miedo a las complicaciones a largo plazo es algo que nadie
            debería enfrentar sin una estrategia clara.{' '}
            <span className="font-bold text-[#0D6E6E]" data-id="element-159">No es tu culpa.</span> La
            mayoría de los consejos que recibimos son demasiado teóricos o
            difíciles de mantener. Lo que necesitas no es más información, sino
            un plan de choque que funcione hoy mismo."
          </p>
        </div>

        {/* The Problem */}
        <div className="mb-16" data-id="element-160">
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#1A1A2E] mb-6 text-center" data-id="element-161">
            La agotadora <span className="text-[#0D6E6E]" data-id="element-162">"Montaña Rusa"</span>{' '}
            de la Glucosa
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed mb-8" data-id="element-163">
            Vivir así es agotador. Un día te despiertas con niveles aceptables,
            y al siguiente, sin razón aparente, estás en 210 mg/dL. Sientes
            mareos, vista borrosa y una fatiga que no te deja disfrutar de tu
            familia. Has intentado eliminar todos los carbohidratos, pero el
            hambre te gana. Has probado aplicaciones que te piden registrar cada
            gramo de comida, pero terminas más estresado que al principio.
          </p>
          <div className="bg-[#1A1A2E] text-white p-6 rounded-xl border-l-4 border-[#D4A017]" data-id="element-164">
            <p className="text-lg font-medium" data-id="element-165">
              El problema es que estás tratando de luchar contra tu biología
              solo con fuerza de voluntad, sin darle a tu cuerpo los aliados
              naturales que necesita para procesar el azúcar correctamente.
            </p>
          </div>
        </div>

        {/* Why previous attempts failed */}
        <div data-id="element-166">
          <h3 className="text-2xl font-bold text-[#1A1A2E] mb-8 text-center" data-id="element-167">
            Por qué lo que has intentado{' '}
            <span className="text-red-500" data-id="element-168">no funcionó</span>:
          </h3>

          <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true,
          margin: '-50px'
        }} className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-169">
            {failedAttempts.map((item, index) => <motion.div key={index} variants={itemVariants} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex gap-4 items-start hover:shadow-md transition-shadow" data-id="element-170">
                <div className="bg-red-50 p-3 rounded-lg shrink-0" data-id="element-171">
                  <item.icon className="w-6 h-6 text-red-500" data-id="element-172" />
                </div>
                <p className="text-gray-700 font-medium leading-relaxed" data-id="element-173">
                  {item.text}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </div>
    </SectionReveal>;
}